import type { ReactNode, Dispatch, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useState } from 'react'

import _ from 'lodash'
import { toast } from 'sonner'


import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { showErrorAlert } from '@/shared/lib/errors'
import { alert } from '@/shared/ui/dialog'
import { truncateName } from '@/shared/lib/string/truncate-name'
import { api } from '@/shared/lib/api'
import { getRandomConfig as getRandomAvatarConfig } from '@/shared/ui/Avatar'

type AvatarConfig = Record<string, unknown>

export interface AvatarContextType {
  hasAvatar: boolean
  hasUnsavedChanges: boolean
  noAvatar: boolean
  avatar: AvatarConfig | undefined
  changeAvatar: Dispatch<SetStateAction<AvatarConfig | undefined>>
  saveAvatar: (config: AvatarConfig) => Promise<void>
}

const AvatarContext = createContext<AvatarContextType | null>(null)

export function useAvatarContext() {
  const context = useContext(AvatarContext)
  if (!context) {
    throw new Error('useAvatarContext must be used within an AvatarProvider')
  }
  return context
}

interface AvatarProviderProps {
  children: ReactNode
}

export function AvatarProvider({ children }: AvatarProviderProps) {
  const { navigateTo } = useNavigation()

  const [avatar, setAvatar] = useState<AvatarConfig | undefined>(undefined)

  const { userProgress, refetchProgress } = useUserProgress()

  const {
    config: { apiBasePathWithId }
  } = useDigitalProductContext()

  const hasAvatar = !!avatar && !_.isEmpty(avatar)
  const noAvatar =
    !!userProgress && (!userProgress?.avatar || _.isEmpty(userProgress.avatar))
  const hasUnsavedChanges =
    hasAvatar && !_.isEqual(avatar, userProgress?.avatar)

  const _saveAvatar = async (config: AvatarConfig) =>
    await api.put(`${apiBasePathWithId}/progress`, {
      record: {
        avatar: config
      }
    })

  const saveAvatar = async (config: AvatarConfig) => {
    if (!config || _.isEmpty(config)) return

    try {
      await _saveAvatar({ ...config, _generatedBySystem: false })

      if (noAvatar) {
        const userName = truncateName(_.get(userProgress, 'user.name', ''), 20)
        await alert({
          variant: 'success',
          title: 'Avatar salvo',
          description: `Excelente, ${userName}! Seu avatar foi salvo com sucesso. Agora podemos continuar sua aventura...`,
          confirmLabel: 'Continuar'
        })
        navigateTo('/?tour=1')
      } else {
        toast.success('Avatar atualizado com sucesso!')
      }

      refetchProgress()
    } catch (error) {
      showErrorAlert(error)
    }
  }

  useEffect(() => {
    if (userProgress) {
      const avatar = _.merge(
        getRandomAvatarConfig(),
        userProgress?.avatar || {}
      )
      setAvatar(avatar)

      if (noAvatar) {
        try {
          _saveAvatar({ ...avatar, _generatedBySystem: true })
          refetchProgress()
        } catch (error) {}
      }
    }
  }, [userProgress, noAvatar])

  return (
    <AvatarContext.Provider
      value={{
        hasAvatar,
        hasUnsavedChanges,
        noAvatar,
        avatar,
        changeAvatar: setAvatar,
        saveAvatar
      }}
    >
      {children}
    </AvatarContext.Provider>
  )
}
